import ForgeVideoPost from "../models/ForgeVideoPost";
import {decorate, observable, computed, action} from "mobx";
import {CMS_DESTINATION, CMS_DESTINATION_LABEL, FIELD_TO_PITS} from "../utils/constants";
import _ from "lodash";
import {toast} from "react-toastify";
import React from "react";
import SentToForgeToast from "../components/SentToForgeToast";

const FORGE_BASE_URLS = {
  [CMS_DESTINATION.MLB]: process.env.REACT_APP_FORGE_BASE_URL,
  [CMS_DESTINATION.MILB]: process.env.REACT_APP_FORGE_MILB_BASE_URL
};

class SendToForgeDialogStore {
  forgeVideoPost = {};
  clip = {};
  isOpen = false;
  loadingMap = new Map();
  clipsToSearch = [];
  selectedTags = [];
  sendingDestination = null;
  destinationsBeingSent = [];
  selectedDestinations = {
    [CMS_DESTINATION.MLB]: true,
    [CMS_DESTINATION.MILB]: false
  };

  constructor(
      pitsApi,
      clippyApi,
      jaredApi,
      typeaheadDropdownStore,
      gameMetadataStore,
      forgeSnackBarStore,
      authStore,
      experienceStore
  ) {
    this.pitsApi = pitsApi;
    this.clippyApi = clippyApi;
    this.jaredApi = jaredApi;
    this.typeaheadDropdownStore = typeaheadDropdownStore;
    this.gameMetadataStore = gameMetadataStore;
    this.forgeSnackBarStore = forgeSnackBarStore;
    this.authStore = authStore;
    this.experienceStore = experienceStore;
  }

  async openDialog(clipId) {
    this.resetDestinations();
    await this.initFields(clipId);
    this.isOpen = true;
  }

  async initFields(clipId) {
    try {
      this.loadingMap.set(clipId, true);
      this.clip = this.clipsToSearch.find(clip => clip.videoId === clipId);
      const commonFields = await this.pitsApi.getCommonFields(
          this.clip.playIds
      );
      const playbackOptions = await this.getPlaybackOptions();
      this.forgeVideoPost = new ForgeVideoPost(
          this.clip,
          commonFields,
          playbackOptions
      );
      this.createTags(this.clip.commonClipSearchMetadata);
      this.loadingMap.delete(this.clip.videoId);
    } catch (err) {
      this.loadingMap.delete(clipId);
      console.log(err);
      alert("Problem initializing fields, please try again.");
    }
  }

  /**
   * @param searchMetadata: Search Metadata object.  Specifies which player/team tags to use
   * Forge supports the following tags:  season, gamepk, playerid, teamid.
   * Check commonFields to see if they're included and if so add the tag
   */
  createTags(searchMetadata) {
    this.addGeneralTags();
    if (searchMetadata.category === "Batter") {
      this.addBatterTags();
    } else if (searchMetadata.category === "Pitcher") {
      this.addPitcherTags();
    } else if (searchMetadata.category === "Fielder") {
      this.addFielderTags(searchMetadata);
    } else if (searchMetadata.category === "Runner") {
      this.addRunnerTags(searchMetadata);
    }
  }

  addGeneralTags() {
    const seasonMapping = FIELD_TO_PITS["SEASON"];
    const gamePkMapping = FIELD_TO_PITS["GAME_PK"];
    if (this.forgeVideoPost.commonFields[seasonMapping]) {
      const slug = `season-${this.forgeVideoPost.commonFields[seasonMapping]}`;
      this.addTag({label: slug, value: slug});
    }

    if (this.forgeVideoPost.commonFields[gamePkMapping]) {
      const slug = `gamepk-${this.forgeVideoPost.commonFields[gamePkMapping]}`;
      this.addTag({label: slug, value: slug});
    }
  }

  addBatterTags() {
    const batterPlayerIdMapping = FIELD_TO_PITS["BATTER_PLAYER_ID"];
    const batterPlayerNameMapping = FIELD_TO_PITS["BATTER_PLAYER_NAME"];
    if (this.forgeVideoPost.commonFields[batterPlayerIdMapping]) {
      const slug = `playerid-${this.forgeVideoPost.commonFields[batterPlayerIdMapping]}`;
      const display = `${this.forgeVideoPost.commonFields[batterPlayerNameMapping]} (${slug})`;
      this.addTag({label: display, value: slug});
    }
    const battingTeamNameMapping = FIELD_TO_PITS["BATTING_TEAM_NAME"];
    const battingTeamIdMapping = FIELD_TO_PITS["BATTING_TEAM_ID"];
    if (this.forgeVideoPost.commonFields[battingTeamIdMapping]) {
      const slug = `teamid-${this.forgeVideoPost.commonFields[battingTeamIdMapping]}`;
      const display = `${this.forgeVideoPost.commonFields[battingTeamNameMapping]} (${slug})`;
      this.addTag({label: display, value: slug});
    }
  }

  addPitcherTags() {
    const pitcherPlayerNameMapping = FIELD_TO_PITS["PITCHER_PLAYER_NAME"];
    const pitcherPlayerIdMapping = FIELD_TO_PITS["PITCHER_PLAYER_ID"];
    if (this.forgeVideoPost.commonFields[pitcherPlayerIdMapping]) {
      const slug = `playerid-${this.forgeVideoPost.commonFields[pitcherPlayerIdMapping]}`;
      const display = `${this.forgeVideoPost.commonFields[pitcherPlayerNameMapping]} (${slug})`;
      this.addTag({label: display, value: slug});
    }
    const pitchingTeamNameMapping = FIELD_TO_PITS["PITCHING_TEAM_NAME"];
    const pitchingTeamIdMapping = FIELD_TO_PITS["PITCHING_TEAM_ID"];
    if (this.forgeVideoPost.commonFields[pitchingTeamIdMapping]) {
      const slug = `teamid-${this.forgeVideoPost.commonFields[pitchingTeamIdMapping]}`;
      const display = `${this.forgeVideoPost.commonFields[pitchingTeamNameMapping]} (${slug})`;
      this.addTag({label: display, value: slug});
    }
  }

  addFielderTags(searchMetadata) {
    const {fieldName} = searchMetadata;
    const fielderPlayerIdMapping = FIELD_TO_PITS.FIELDER[fieldName];
    if (searchMetadata.fieldName) {
      if (this.forgeVideoPost.commonFields[fielderPlayerIdMapping]) {
        const slug = `playerid-${this.forgeVideoPost.commonFields[fielderPlayerIdMapping]}`;
        this.addTag({label: slug, value: slug});
      }
    }
    const pitchingTeamNameMapping = FIELD_TO_PITS["PITCHING_TEAM_NAME"];
    const pitchingTeamIdMapping = FIELD_TO_PITS["PITCHING_TEAM_ID"];
    if (this.forgeVideoPost.commonFields[pitchingTeamIdMapping]) {
      const slug = `teamid-${this.forgeVideoPost.commonFields[pitchingTeamIdMapping]}`;
      const display = `${this.forgeVideoPost.commonFields[pitchingTeamNameMapping]} (${slug})`;
      this.addTag({label: display, value: slug});
    }
  }

  addRunnerTags(searchMetadata) {
    const {fieldName} = searchMetadata;
    const runnerField = this.getRunnerField(fieldName);
    const runnerIdMapping = FIELD_TO_PITS[runnerField];
    if (searchMetadata.fieldName) {
      if (this.forgeVideoPost.commonFields[runnerIdMapping]) {
        const slug = `playerid-${this.forgeVideoPost.commonFields[runnerIdMapping]}`;
        this.addTag({label: slug, value: slug});
      }
    }
    const battingTeamIdMapping = FIELD_TO_PITS["BATTING_TEAM_ID"];
    const battingTeamNameMapping = FIELD_TO_PITS["BATTING_TEAM_NAME"];
    if (this.forgeVideoPost.commonFields[battingTeamIdMapping]) {
      const slug = `teamid-${this.forgeVideoPost.commonFields[battingTeamIdMapping]}`;
      const display = `${this.forgeVideoPost.commonFields[battingTeamNameMapping]} (${slug})`;
      this.addTag({label: display, value: slug});
    }
  }

  addTag(tag) {
    const index = _.findIndex(this.selectedTags, function (o) {
      return o.value === tag.value;
    });
    if (index === -1) {
      this.selectedTags.push(tag);
      this.typeaheadDropdownStore.addPreviousSelections([tag]);
    }
  }

  getRunnerField(fieldName) {
    if (!fieldName) {
      return;
    }
    if (fieldName.includes("First")) {
      return "RUNNER_ON_FIRST_ID";
    } else if (fieldName.includes("Second")) {
      return "RUNNER_ON_SECOND_ID";
    } else if (fieldName.includes("Third")) {
      return "RUNNER_ON_THIRD_ID";
    }
  }

  async sendVideo() {
    this.loadingMap.set(this.clip.videoId, true);
    this.isOpen = false;

    // 0. Assign all selected CMSes against the desired Publish body
    const destinations = this.selectedDestinationNames;
    const requestBodies = destinations.map(destination => ({
      ...this.forgeVideoRequestBody,
      destinationName: destination
    }));

    // 1. Snapshot the assigned destinations so we can refer to them post-reset()
    this.setDestinationsBeingSent(destinations);

    // 2. Physically post to Forge. Tracks request in-flight for displaying as part
    //    of progress indicator.
    const forgeLinks = [];
    const failedDestinations = [];
    for (let i = 0; i < destinations.length; i++) {
      const destination = destinations[i];
      this.setSendingDestination(destination);
      try {
        await this.jaredApi.postToForge(
            requestBodies[i],
            this.authStore.testGroupId
        );
        forgeLinks.push({
          destination,
          url: `${FORGE_BASE_URLS[destination]}/#/wcm/custom/video/${this.forgeVideoPost.videoId}`
        });
      } catch (err) {
        failedDestinations.push(destination);
      }
    }
    this.setSendingDestination(null);
    this.setDestinationsBeingSent([]);
    this.loadingMap.delete(this.clip.videoId);

    if (forgeLinks.length > 0) {
      this.clip.forgeUrl = forgeLinks[0].url;
      this.forgeSnackBarStore.handleOpen(forgeLinks);
    }

    forgeLinks.forEach(link => {
      toast.success(<SentToForgeToast link={link}/>, {
        position: "top-right",
        autoClose: 10000
      });
    });

    failedDestinations.forEach(destination => {
      toast.error(
          `Failed to post to ${CMS_DESTINATION_LABEL[destination]}. Please try again.`,
          {position: "top-right"}
      );
    });
  }

  setSendingDestination(destination) {
    this.sendingDestination = destination;
  }

  setDestinationsBeingSent(destinations) {
    this.destinationsBeingSent = destinations;
  }

  async getPlaybackOptions() {
    const response = await this.clippyApi.getOutputTypes(this.clip.videoId);
    const {linksByType} = response;
    return linksByType.map(linkByType => {
      return {
        clipOutputType: linkByType.videoType,
        url: linkByType.link
      };
    });
  }

  get forgeVideoRequestBody() {
    // Don't send the playId if the clip includes more than 1 play
    const playId =
        this.forgeVideoPost.playIds.length === 1
            ? this.forgeVideoPost.playIds[0]
            : "";
    return {
      videoTitle: this.forgeVideoPost.videoTitle,
      videoDescription: this.forgeVideoPost.videoDescription,
      videoId: this.forgeVideoPost.videoId,
      videoUrl: this.forgeVideoPost.videoUrl,
      thumbnailUrl: this.forgeVideoPost.thumbnailUrl,
      currentGamePk: this.forgeVideoPost.currentGamePk,
      durationInSeconds: this.forgeVideoPost.durationInSeconds,
      tags: this.selectedTags.map(t => t.value),
      playbackOptions: this.forgeVideoPost.playbackOptions,
      playId
    };
  }

  // The Forge destinations the user may post to, given the active experience:
  // * MLB -> [MLB]
  // * MiLB -> [MLB, MiLB]
  get availableDestinations() {
    if (this.experienceStore.isMilbExperience) {
      return [CMS_DESTINATION.MLB, CMS_DESTINATION.MILB];
    }
    return [CMS_DESTINATION.MLB];
  }

  get selectedDestinationNames() {
    return this.availableDestinations.filter(
        destination => this.selectedDestinations[destination]
    );
  }

  // At least one available destination must be selected before a clip can be sent.
  get hasValidDestinationSelection() {
    return this.selectedDestinationNames.length > 0;
  }

  isDestinationSelected(destination) {
    return (
        this.availableDestinations.includes(destination) &&
        !!this.selectedDestinations[destination]
    );
  }

  toggleDestination(destination) {
    if (!this.availableDestinations.includes(destination)) {
      return;
    }
    this.selectedDestinations[destination] = !this.selectedDestinations[destination];
  }

  resetDestinations() {
    const isMilb = this.experienceStore.isMilbExperience;
    this.selectedDestinations = {
      [CMS_DESTINATION.MLB]: !isMilb,
      [CMS_DESTINATION.MILB]: isMilb
    };
  }

  isLoading(videoId) {
    return this.loadingMap.get(videoId);
  }

  reset() {
    this.isOpen = false;
    this.selectedTags = [];
    this.resetDestinations();
  }
}

decorate(SendToForgeDialogStore, {
  isOpen: observable,
  loadingMap: observable,
  clipsToSearch: observable,
  selectedTags: observable,
  selectedDestinations: observable,
  sendingDestination: observable,
  destinationsBeingSent: observable,
  forgeVideoRequestBody: computed,
  availableDestinations: computed,
  selectedDestinationNames: computed,
  hasValidDestinationSelection: computed,
  sendVideo: action,
  setSendingDestination: action,
  setDestinationsBeingSent: action,
  addTag: action,
  toggleDestination: action,
  resetDestinations: action,
  reset: action
});

export default SendToForgeDialogStore;



// WEBPACK FOOTER //
// ./src/stores/SendToForgeDialogStore.js